import React from 'react';
import { Calendar, CreditCard, RefreshCw, Download, FileText } from 'lucide-react';
import Button from '../../../components/ui/Button';

const DetailedReceipt = ({ subscription, planAmount, paymentMethod }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const billingIntervalLabel = subscription?.billingInterval === 'yearly' ? 'year' : 'month';
  const transactionId = `TXN-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}`;
  const planLabel = subscription?.plan?.planType === 'premium'
    ? `MyBodyToday Premium — £${planAmount}/month`
    : subscription?.plan?.name;

  const handleDownloadReceipt = () => {
    const receiptData = `
MYBODYTODAY - PAYMENT RECEIPT
${'='?.repeat(60)}

Transaction ID: ${transactionId}
Transaction Date: ${new Date()?.toLocaleString()}

PLAN DETAILS:
Plan Name: ${subscription?.plan?.name}
Billing Interval: ${subscription?.billingInterval}
Amount Paid: £${planAmount} GBP

PAYMENT INFORMATION:
Payment Method: ${paymentMethod || 'Card ending in ****'}
Payment Status: Completed

BILLING PERIOD:
Start Date: ${formatDate(subscription?.currentPeriodStart)}
End Date: ${formatDate(subscription?.currentPeriodEnd)}

NEXT RENEWAL:
Renewal Date: ${formatDate(subscription?.currentPeriodEnd)}
Renewal Amount: £${planAmount} GBP

${'='?.repeat(60)}
MyBodyToday - Symptom Tracking Tool
Questions? Contact support@mybodytoday1.com
Thank you for your subscription!
    `;

    const blob = new Blob([receiptData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mybodytoday-receipt-${new Date()?.toISOString()?.split('T')?.[0]}.txt`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-600" />
          Transaction Receipt
        </h2>
        <Button 
          onClick={handleDownloadReceipt}
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
        >
          Download Receipt
        </Button>
      </div>
      <div className="space-y-4">
        {/* Transaction ID */}
        <div className="pb-4 border-b border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Transaction ID</p>
          <p className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-2 rounded">{transactionId}</p>
        </div>

        {/* Plan & Amount */}
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <div>
            <p className="text-sm text-gray-600">Plan</p>
            <p className="text-lg font-semibold text-gray-900">{planLabel}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Amount Paid</p>
            <p className="text-3xl font-bold text-green-600">£{planAmount}</p>
            <p className="text-xs text-gray-500">per {billingIntervalLabel}</p>
          </div>
        </div>

        {/* Payment Method */}
        <div className="flex items-start">
          <CreditCard className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Payment Method</p>
            <p className="text-sm text-gray-600">{paymentMethod || 'Card ending in ****'}</p>
            <p className="text-xs text-green-600 mt-1">✓ Payment Successful</p>
          </div>
        </div>

        {/* Billing Period */}
        <div className="flex items-start">
          <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Current Billing Period</p>
            <p className="text-sm text-gray-600">
              {formatDate(subscription?.currentPeriodStart)} - {formatDate(subscription?.currentPeriodEnd)}
            </p>
          </div>
        </div>

        {/* Next Renewal */}
        <div className="flex items-start">
          <RefreshCw className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Next Renewal Date</p>
            <p className="text-sm text-gray-600">
              {formatDate(subscription?.currentPeriodEnd)}
            </p>
            {subscription?.cancelAtPeriodEnd ? (
              <p className="text-xs text-orange-600 mt-1">
                ⚠ Subscription will not renew (cancelled)
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                You'll be charged £{planAmount} on this date
              </p>
            )}
          </div>
        </div>

        {/* Transaction Date & Status */}
        <div className="pt-4 border-t border-gray-200 bg-green-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-700 font-medium">Transaction Date</span>
            <span className="text-gray-900">{formatDate(new Date()?.toISOString())}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-700 font-medium">Status</span>
            <span className="text-green-600 font-semibold flex items-center gap-1">
              <span className="w-2 h-2 bg-green-600 rounded-full"></span>
              Completed
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedReceipt;